import type {
  MCDApplication,
  MCDOffer,
  MCDDeals,
  MCDUser,
  MCDLanguage,
  MCDCountry,
  MCDAuth
} from './types';

import { isDEV } from '@/utils/Location';

export const app = window as MCDApplication;

export const activateOffer = (loyalty: number, reward: number) =>
  isDEV()
    ? new Promise((resolve) => resolve(undefined))
    : new Promise((resolve, reject) => {
      const offer = app.mcd.bridge.message('offerActivation') as MCDOffer;

      offer.send({
        autoActivate: false,
        loyaltyId: loyalty,
        rewardId: reward
      });

      offer.on('done', () => console.info('"activateOffer" Done!'));
      offer.on('error', (error) => reject(error));
      offer.on('data', (data) => resolve(data));
    });

export const burnUserPoints = (quantity: number) =>
  isDEV()
    ? new Promise((resolve) => resolve(undefined))
    : new Promise((resolve, reject) => {
      const deals = app.mcd.bridge.message('deals') as MCDDeals;
      deals.send({ burnPoints: true, points: -quantity });

      deals.on('done', () => console.info('"burnUserPoints" Done!'));
      deals.on('data', (data) => resolve(data.points));
      deals.on('error', (error) => reject(error));
    });

export const addPlexureTags = (tags: string[]) =>
  isDEV()
    ? new Promise((resolve) => resolve(undefined))
    : new Promise((resolve, reject) => {
      const user = app.mcd.bridge.message('user') as MCDUser;
      user.send({ addTags: tags });

      user.on('done', () => console.info('"addPlexureTags" Done!'));
      user.on('error', (error) => reject(error));
      user.on('data', (data) => resolve(data));
    });

export const removePlexureTags = (tags: string[]) =>
  isDEV()
    ? new Promise((resolve) => resolve(undefined))
    : new Promise((resolve, reject) => {
      const user = app.mcd.bridge.message('user') as MCDUser;
      user.send({ removeTags: tags });

      user.on('done', () => console.info('"removePlexureTags" Done!'));
      user.on('error', (error) => reject(error));
      user.on('data', (data) => resolve(data));
    });

export const orientationScreen = () => {
  const system = app.mcd.bridge.message('system');
  system.send({ appOrientation: 'portrait' });
};

export const toggleFullscreen = () => {
  const system = app.mcd.bridge.message('system');
  system.send({ hideCloseButton: true, fullscreen: true });
};

export const getUserLanguage = () =>
  isDEV()
    ? new Promise((resolve) =>
      resolve(import.meta.env.VITE_USER_LANGUAGE || 'en')
    )
    : new Promise((resolve, reject) => {
      const system = app.mcd.bridge.message('system') as MCDLanguage;
      system.send({ getSelectedLanguage: true });

      system.on('done', () => console.info('"getUserLanguage" Done!'));
      system.on('data', (data) => resolve(data.language));
      system.on('error', (error) => reject(error));
    });

export const getUserCountry = () =>
  isDEV()
    ? new Promise((resolve) =>
      resolve(import.meta.env.VITE_USER_COUNTRY || 'us')
    )
    : new Promise((resolve, reject) => {
      const system = app.mcd.bridge.message('system') as MCDCountry;
      system.send({ getMarketId: true });

      system.on('done', () => console.info('"getUserCountry" Done!'));
      system.on('data', (data) => resolve(data.marketId));
      system.on('error', (error) => reject(error));
    });

export const getUserPoints = () =>
  isDEV()
    ? new Promise((resolve) => resolve(Infinity))
    : new Promise((resolve, reject) => {
      const deals = app.mcd.bridge.message('deals') as MCDDeals;
      deals.send({ getPoints: true });

      deals.on('done', () => console.info('"getUserPoints" Done!'));
      deals.on('data', (data) => resolve(data.points));
      deals.on('error', () => reject(4001));
    });

export const getUserData = () =>
  isDEV()
    ? new Promise((resolve) =>
      resolve({
        firstname: 'John',
        lastname: 'Carmack',
        email: 'carmack@idsoftware.com',
        deviceId: 'bfg_666',
        mcdonaldsId:
          'Bearer ue7esvu4ae3bihigoeopgydoliyjttay7vx8gktcltrkgjcx4nwo3paocgze4qas'
      })
    )
    : new Promise((resolve, reject) => {
      const user = app.mcd.bridge.message('user') as MCDUser;
      user.send({ getuser: true, promptlogin: true });

      user.on('done', () => console.info('"getUserData" Done!'));
      user.on('data', (data) => resolve(data));
      user.on('error', () => reject(4001));
    });

export const promptLogin = () =>
  isDEV()
    ? new Promise((resolve) => resolve(undefined))
    : new Promise((resolve, reject) => {
      const user = app.mcd.bridge.message('user') as MCDUser;
      user.send({ promptlogin: true });

      user.on('done', () => console.info('"promptLogin" Done!'));
      user.on('error', (error) => reject(error));
      user.on('data', (data) => resolve(data));
    });

export const getAppVersion = () =>
  isDEV()
    ? new Promise((resolve) =>
      resolve({
        version: '3.0.127391.stage',
        platform: 'android',
        build: '27391'
      })
    )
    : new Promise((resolve, reject) => {
      const system = app.mcd.bridge.message('system') as MCDUser;
      system.send({ getVersion: true });

      system.on('done', () => console.info('"getAppVersion" Done!'));
      system.on('error', (error) => reject(error));
      system.on('data', (data) => resolve(data));
    });

export const getAuthCode = (clientId: string) =>
  isDEV()
    ? new Promise<undefined>((resolve) => resolve(void 0))
    : new Promise<string>((resolve, reject) => {
      const user = app?.mcd?.bridge?.message?.('user') as MCDAuth
      if (!user) {
        reject(new Error('Bridge not available'))
        return
      }

      let resolved = false
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true
          reject(new Error('Timeout while waiting for auth code'))
        }
      }, 10000)

      user.send({ getAuthCode: true, clientId })

      user.on('data', (data) => {
        if (resolved) return
        resolved = true
        clearTimeout(timeout)
        if (!data?.authCode || typeof data.authCode !== 'string') {
          reject(new Error('Invalid authCode received'))
          return
        }
        resolve(data.authCode);
      })
      user.on('error', (error) => {
        if (resolved) return
        resolved = true
        clearTimeout(timeout)
        reject(error)
      })
      user.on('done', () => {
        console.info('"getCasAuthToken" Done!');
      })
    })
